import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { useQuestions } from '@/contexts/QuestionsContext';
import { useProject } from '@/contexts/ProjectContext';
import { useThreatCat } from '@/contexts/ThreatCatContext';
import { projectService } from '@/services/projectService';
import { queryKeys } from '@/lib/queryKeys';
import { useProjectSync } from '@/hooks/useProjectSync';
import { normalizeData } from '@/lib/parsers';
import { ApiError, ThreatCatAuthError } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { PageSkeleton } from '@/components/ui/page-skeleton';
import ConnectModal from '@/components/threatcat/ConnectModal';
import MainLayout from '@/components/layout/MainLayout';
import TopBar from '@/components/layout/TopBar';
import { toast } from 'sonner';

export default function ProjectLoader() {
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projectId = projectIdParam ? Number(projectIdParam) : NaN;
  const { setAppState } = useApp();
  const { loadQuestions } = useQuestions();
  const { cookie, disconnect, isConnected, projects } = useThreatCat();
  const { detail, syncState, syncError, selectProject } = useProject();
  const { syncProject } = useProjectSync();
  const [connectOpen, setConnectOpen] = useState(false);
  const inFlightSync = useRef<string | null>(null);
  const syncFailed = useRef<string | null>(null);
  const location = useLocation();
  const routeState = location.state as {
    remoteVersionId?: unknown;
    synchronizedDetailUpdatedAt?: unknown;
  } | null;
  const routeRemoteVersionId = routeState?.remoteVersionId;
  const remoteVersionId = typeof routeRemoteVersionId === 'number' && Number.isInteger(routeRemoteVersionId)
    ? routeRemoteVersionId
    : projects.find((project) => project.project_id === projectId)?.id ?? null;

  const detailQuery = useQuery({
    queryKey: queryKeys.projects.detail(projectId),
    queryFn: () => projectService.get(projectId),
    enabled: Number.isInteger(projectId),
    retry: (failureCount, error) => (
      !(error instanceof ApiError && [401, 403, 404].includes(error.status))
      && failureCount < 2
    ),
  });

  // A picker-prepared snapshot needs no second sync. Reloads without that cache still synchronize.
  const autoSynced = useRef<string | null>(
    detailQuery.data?.snapshot && routeState?.synchronizedDetailUpdatedAt === detailQuery.dataUpdatedAt
      ? `${projectId}:${detailQuery.data.project_version_id}`
      : null,
  );

  const runSync = useCallback(async (versionId: number) => {
    const key = `${projectId}:${versionId}`;
    if (inFlightSync.current === key) return detailQuery.data;
    inFlightSync.current = key;
    syncFailed.current = null;
    // Mark this key as attempted regardless of which caller dispatched it
    // (the 403-recovery effect below, the auto-sync effect, a manual
    // retry, or ConnectModal). Without this, a sync started by one path
    // while `detailQuery.data` is still undefined (e.g. during the initial
    // 403) doesn't set `autoSynced`, so once the sync succeeds and
    // `detailQuery.data` first appears, the auto-sync effect fires its own
    // redundant second sync against ThreatCat for the same key.
    autoSynced.current = key;
    const toastId = toast.loading('Synchronizing project...');
    try {
      const updatedDetail = await syncProject({
        projectId,
        projectVersionId: versionId,
      });
      inFlightSync.current = null;
      toast.dismiss(toastId);
      return updatedDetail;
    } catch (error) {
      inFlightSync.current = null;
      syncFailed.current = key;
      if (error instanceof ThreatCatAuthError) disconnect();
      toast.error(error instanceof Error ? error.message : 'Synchronization failed', { id: toastId });
      if (!detailQuery.data?.snapshot) {
        navigate('/projects', { replace: true });
      }
      throw error;
    }
  }, [detailQuery, disconnect, navigate, projectId, syncProject]);

  useEffect(() => {
    if (!(detailQuery.error instanceof ApiError) || detailQuery.error.status !== 403) return;
    if (cookie && remoteVersionId) {
      const key = `${projectId}:${remoteVersionId}`;
      if (inFlightSync.current === key) {
        return;
      }
      if (syncFailed.current !== key) {
        void runSync(remoteVersionId).catch(() => undefined);
        return;
      }
    }
    if (syncFailed.current !== `${projectId}:${remoteVersionId}`) {
      toast.error('You no longer have access to this project.');
    }
    navigate('/projects', { replace: true });
  }, [cookie, detailQuery.error, navigate, projectId, remoteVersionId, runSync]);

  useEffect(() => {
    if (!detailQuery.data) return;
    const loadedDetail = detailQuery.data;
    selectProject(loadedDetail);
    if (loadedDetail.snapshot) {
      setAppState(normalizeData(loadedDetail.snapshot));
      void loadQuestions(loadedDetail.project_id);
    }
  }, [detailQuery.data, loadQuestions, selectProject, setAppState]);

  useEffect(() => {
    const loadedDetail = detailQuery.data;
    if (!loadedDetail || !cookie) return;
    // Wait for the catalog so an older version's export can't race a newer one.
    const catalogResolved = typeof routeRemoteVersionId === 'number' || isConnected;
    if (!catalogResolved) return;
    const versionId = remoteVersionId ?? loadedDetail.project_version_id;
    if (!versionId) return;

    // Auto-sync at most once per (project, version). `runSync` invalidates
    // the `projects.detail` query on success, which makes `detailQuery.data`
    // change identity and re-run this effect — without this guard that
    // creates an infinite sync loop (sync -> invalidate -> refetch -> sync -> ...).
    const key = `${projectId}:${versionId}`;
    if (autoSynced.current === key) return;
    autoSynced.current = key;

    const synchronize = async () => {
      try {
        await runSync(versionId);
      } catch (error) {
        if (!(error instanceof ThreatCatAuthError)) return;
      }
    };

    void synchronize();
  }, [cookie, detailQuery.data, isConnected, projectId, remoteVersionId, routeRemoteVersionId, runSync]);

  if (!Number.isInteger(projectId)) {
    return <Navigate to="/projects" replace />;
  }

  const currentDetail = detail?.project_id === projectId ? detail : null;
  const localAccessDenied = detailQuery.error instanceof ApiError && detailQuery.error.status === 403;
  if (detailQuery.isLoading || localAccessDenied || (!currentDetail?.snapshot && syncState === 'syncing')) {
    return <PageSkeleton />;
  }

  if (detailQuery.error) {
    const error = detailQuery.error instanceof Error ? detailQuery.error.message : 'Failed to load project';
    return (
      <div className="min-h-screen bg-background">
        <TopBar />
        <div className="container py-12 text-center space-y-4">
          <h2 className="text-xl font-semibold text-destructive">Unable to load project</h2>
          <p className="text-muted-foreground">{error}</p>
          <Button onClick={() => {
            inFlightSync.current = null;
            syncFailed.current = null;
            void detailQuery.refetch();
          }}>Retry</Button>
        </div>
      </div>
    );
  }

  if (!currentDetail) {
    return <PageSkeleton />;
  }

  const syncVersionId = remoteVersionId ?? currentDetail?.project_version_id ?? null;
  const needsSync = !currentDetail?.snapshot && (!cookie || !currentDetail?.project_version_id);
  const syncKey = `${projectId}:${syncVersionId}`;
  const firstSyncPending = !currentDetail?.snapshot && !!cookie && !!syncVersionId
    && (syncState !== 'idle' || inFlightSync.current === syncKey);
  const requiresSynchronization = needsSync || firstSyncPending;
  const retrySync = async () => {
    if (!cookie || !syncVersionId) {
      await detailQuery.refetch();
      return;
    }
    inFlightSync.current = null;
    syncFailed.current = null;
    try {
      await runSync(syncVersionId);
    } catch {
      // Keep the requires-synchronization screen visible with the retryable error.
    }
  };
  if (requiresSynchronization) {
    return (
      <div className="min-h-screen bg-background">
        <TopBar />
        <div className="container py-12 text-center space-y-4">
          <h2 className="text-xl font-semibold">Requires synchronization</h2>
          <p className="text-muted-foreground">
            This project has no local content yet. Connect to ThreatCat to synchronize it.
          </p>
          {syncError && <p className="text-sm text-destructive">{syncError}</p>}
          <div className="flex justify-center gap-2">
            <Button onClick={() => setConnectOpen(true)}>Connect ThreatCat</Button>
            <Button variant="outline" onClick={() => void retrySync()}>Retry</Button>
          </div>
        </div>
        <ConnectModal
          open={connectOpen}
          onClose={() => setConnectOpen(false)}
          onConnected={async () => {
            if (syncVersionId) await runSync(syncVersionId);
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <MainLayout />
    </div>
  );
}
